import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { MapPin, Map as MapIcon } from 'lucide-react';
import type { PublisherConfig, SelfServiceFormData } from '../../../types/selfService';
import { effectiveMinBudget } from '../../../types/selfService';
import { isSelfServiceDevelopment } from '../../../lib/env';
import MarketSelector from '../shared/MarketSelector';
import ImpressionsCalculator from '../shared/ImpressionsCalculator';
import GeoTargetingTabs from '../shared/GeoTargetingTabs';
import { formatMoneySmart } from '../shared/format';
import { FloatingField, FieldCard, FieldError, StepHeader, SectionHeading, accentVars, tintFromAccent } from './_shared';
import DateRangeField from './DateRangeField';
import type { DateRangePreset } from './DateRangePopover';
import InventoryDiagnostic from '../InventoryDiagnostic';
import type { UseCampaignFeasibilityResult } from '../../../services/useCampaignFeasibility';
import type { UseRadiusRecommendationResult } from '../../../services/useRadiusRecommendation';

interface CampaignSetupStepProps {
  config: PublisherConfig;
  feasibility: UseCampaignFeasibilityResult;
  /** CTV-570 (radius-min v2): slider floor recommendation lifted from the wizard bridge. */
  radiusRecommendation: UseRadiusRecommendationResult;
  bypassFeasibility: boolean;
  onToggleBypassFeasibility: (next: boolean) => void;
}

// Marker for the campaignName collision error type set by the wizard's
// Continue-time check (SelfServiceWizard.handleNext). Watched here so the
// paired clear-on-edit effect can recognize the collision chip and only
// clear that one — not, e.g., a resolver-emitted required-field chip.
const COLLISION_ERROR_TYPE = 'collision';

function formatYmd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const CampaignSetupStep: React.FC<CampaignSetupStepProps> = ({ config, feasibility, radiusRecommendation, bypassFeasibility, onToggleBypassFeasibility }) => {
  const accentColor = config.accentColor || config.primaryColor;
  const { register, control, watch, clearErrors, formState: { errors } } = useFormContext<SelfServiceFormData>();
  const budget = watch('budget');
  const startDate = watch('startDate');
  const endDate = watch('endDate');
  const selectedProduct = watch('selectedProduct');
  const campaignName = watch('campaignName');
  // Watched for the dev diagnostic.
  const paymentPlan = watch('paymentPlan');
  const targetingMode = watch('targetingMode');
  const geoMode = watch('geoMode');
  const radiusZips = watch('geoTargetZips');

  // CTV-533 followup: collision detection moved to Continue-time only — the
  // wizard's validateCurrentStep gate in SelfServiceWizard.handleNext owns the
  // imperative setError now. This watcher is the paired clear half of the
  // imperative + watcher pattern from ADR-0012: once a collision chip is
  // showing, clear it the moment the buyer edits the name or moves the start
  // date (which changes the fallback name and so could resolve the collision).
  React.useEffect(() => {
    if (errors.campaignName?.type === COLLISION_ERROR_TYPE) {
      clearErrors('campaignName');
    }
    // We intentionally don't depend on errors.campaignName?.type — adding it
    // would re-fire the moment the error is cleared and create a no-op loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignName, startDate, clearErrors]);

  // CTV-431: every station sells a single Run-of-Network product. The id
  // is baked into the wizard's seed (see SelfServiceWizard.tsx) so it's
  // present from the first render — no post-mount setValue cascade needed.

  // CTV-533 / ADR-0012: the imperative end-date `setError` / `clearErrors`
  // pair that used to live here was deleted. End-date ordering and min-days
  // rules now live in `campaignSetupSchema.superRefine` so RHF's
  // `reValidateMode: 'onChange'` auto-clears the error the moment the buyer
  // picks a valid window — no watcher needed.

  const activeProduct = config.products.find((p) => p.id === selectedProduct);
  const activeCpm = activeProduct?.cpm ?? 0;

  // Single-market auto-bind for `targetMarkets` is also seeded in the
  // wizard's seed builder — no post-mount setValue cascade needed here.

  // Campaigns need 3 days of lead time before start so the publisher has
  // room to review the creative. Use local-date formatting (matches the
  // preset writers) so timezone offsets don't push minStartDate one day
  // later than the value the preset writes.
  const minStartDate = React.useMemo(() => {
    const earliest = new Date();
    earliest.setDate(earliest.getDate() + 3);
    return formatYmd(earliest);
  }, []);

  // Hide end-side presets that would produce a too-short window — either
  // because the preset's date is <= 3 days from today (no review lead
  // time) or because the chosen start makes the window shorter than
  // minCampaignDays.
  const { showEndOfMonth, showEndOfNextMonth, showEndOfYear } = React.useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const endOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 2, 0);
    const endOfYear = new Date(today.getFullYear(), 11, 31);
    const start = startDate ? new Date(startDate) : null;
    const dayMs = 1000 * 60 * 60 * 24;
    const longEnough = (end: Date) =>
      !start || Math.ceil((end.getTime() - start.getTime()) / dayMs) >= config.minCampaignDays;
    // Independent of the chosen start: the preset's own date must sit
    // far enough in the future that there's at least 4 days of campaign
    // window (matches the 3-day review lead time on the start side).
    const farEnoughFromToday = (end: Date) =>
      Math.ceil((end.getTime() - today.getTime()) / dayMs) > 3;
    return {
      showEndOfMonth: farEnoughFromToday(endOfMonth) && longEnough(endOfMonth),
      showEndOfNextMonth: farEnoughFromToday(endOfNextMonth) && longEnough(endOfNextMonth),
      showEndOfYear: farEnoughFromToday(endOfYear) && longEnough(endOfYear),
    };
  }, [startDate, config.minCampaignDays]);

  return (
    <div className="-m-4 sm:-m-6 md:-m-8" style={accentVars(accentColor, config.primaryColor)}>
      <div className="px-6 sm:px-10 md:px-14 pt-6 pb-6">
        <StepHeader step={2} label="Campaign" accentColor={accentColor} />
      </div>

      <div className="px-6 sm:px-10 md:px-14 pb-10 space-y-8">
        {/* Name + Budget — top of the campaign step. Name is optional and just
            gives the advertiser a label for their own list view. */}
        <FieldCard accentColor={accentColor}>
          <FloatingField id="campaignName" label="Campaign name (optional)" error={errors.campaignName?.message}>
            {(p) => <input {...p} {...register('campaignName')} />}
          </FloatingField>
          <div className="relative">
            <span
              className="pointer-events-none absolute top-3 right-5 z-10 text-[10px] font-medium uppercase tracking-[0.12em] text-slate-400"
              aria-hidden
            >
              Min ${formatMoneySmart(effectiveMinBudget(config.minBudget, isSelfServiceDevelopment()))}
            </span>
          <FloatingField id="budget" label="Campaign budget" required error={errors.budget?.message} prefix="$">
            {(p) => (
              <Controller
                name="budget"
                control={control}
                render={({ field }) => (
                  <input
                    {...p}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={field.value == null ? '' : new Intl.NumberFormat('en-US').format(field.value)}
                    onChange={(e) => {
                      const digitsOnly = e.target.value.replace(/\D/g, '');
                      field.onChange(digitsOnly === '' ? null : Number(digitsOnly));
                    }}
                    onBlur={field.onBlur}
                    placeholder="Campaign budget"
                    className={p.className}
                  />
                )}
              />
            )}
          </FloatingField>
          </div>
        </FieldCard>

        {/* CTV-431: product picker removed — every station is RON-only, the
            single product is auto-selected via the effect above. */}

        {/* Impressions estimate */}
        {budget && budget > 0 && (
          <>
            <ImpressionsCalculator
              budget={budget}
              cpm={activeCpm}
              serviceFeeRates={config.serviceFeeRates}
              accentColor={accentColor}
            />
          </>
        )}

        {/* Dates — CTV-558: single date-range field with a modal range
            picker. Start / end preset chips render under the trigger and
            again inside the modal so the modal is just an elevated view
            of the same controls. */}
        <div>
          <SectionHeading required>Schedule</SectionHeading>
          <Controller
            name="startDate"
            control={control}
            render={({ field: startField }) => (
              <Controller
                name="endDate"
                control={control}
                render={({ field: endField }) => {
                  // Start-side presets: only update startDate. "1st of next
                  // month" hides itself entirely when it would fall before
                  // the earliest start (today + 3) — same hide-when-invalid
                  // treatment as the end-side presets below.
                  const today = new Date();
                  const earliestStart = new Date();
                  earliestStart.setDate(earliestStart.getDate() + 3);
                  const firstOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
                  const showFirstOfNextMonth = firstOfNextMonth >= earliestStart;
                  const startPresets: DateRangePreset[] = [
                    {
                      label: 'Earliest (in 3 days)',
                      apply: () => ({ start: formatYmd(earliestStart) }),
                    },
                  ];
                  if (showFirstOfNextMonth) {
                    startPresets.push({
                      label: '1st of next month',
                      apply: () => ({ start: formatYmd(firstOfNextMonth) }),
                    });
                  }

                  // End-side presets: only update endDate. Hide entries
                  // that would produce a window shorter than
                  // minCampaignDays from the current start.
                  const endPresets: DateRangePreset[] = [];
                  if (showEndOfMonth) {
                    endPresets.push({
                      label: 'End of month',
                      apply: () => {
                        const t = new Date();
                        return {
                          end: formatYmd(new Date(t.getFullYear(), t.getMonth() + 1, 0)),
                        };
                      },
                    });
                  }
                  if (showEndOfNextMonth) {
                    endPresets.push({
                      label: 'End of next month',
                      apply: () => {
                        const t = new Date();
                        return {
                          end: formatYmd(new Date(t.getFullYear(), t.getMonth() + 2, 0)),
                        };
                      },
                    });
                  }
                  if (showEndOfYear) {
                    endPresets.push({
                      label: 'End of year',
                      apply: () => {
                        const t = new Date();
                        return {
                          end: formatYmd(new Date(t.getFullYear(), 11, 31)),
                        };
                      },
                    });
                  }

                  return (
                    <DateRangeField
                      id="campaignDates"
                      label="Campaign dates"
                      required
                      error={errors.startDate?.message ?? errors.endDate?.message}
                      startValue={startField.value}
                      endValue={endField.value}
                      onChange={(next) => {
                        startField.onChange(next.start);
                        endField.onChange(next.end);
                      }}
                      min={minStartDate}
                      minDisabledMessage={`Campaigns need 3 days of review lead time`}
                      startPresets={startPresets}
                      endPresets={endPresets}
                      accentColor={accentColor}
                      primaryColor={config.primaryColor}
                    />
                  );
                }}
              />
            )}
          />
        </div>

        {/* Market */}
        {config.markets.length === 1 ? (
          <SingleMarketTargeting config={config} accentColor={accentColor} feasibility={feasibility.verdict} radiusRecommendation={radiusRecommendation} />
        ) : (
          <MarketSelector markets={config.markets} accentColor={accentColor} />
        )}

        <InventoryDiagnostic
          verdict={feasibility.verdict}
          loading={feasibility.loading}
          error={feasibility.error}
          budget={budget}
          startDate={startDate}
          endDate={endDate}
          paymentPlan={paymentPlan}
          targetingMode={targetingMode}
          geoMode={geoMode}
          geoTargetDMAs={geoMode === 'dmas' ? watch('selectedDMAs') : undefined}
          geoTargetStates={geoMode === 'states' ? watch('selectedStates') : undefined}
          radiusResolvedZips={geoMode === 'radius' ? radiusZips : undefined}
          radiusCenter={geoMode === 'radius' ? watch('geoTargetCenter') ?? null : undefined}
          radiusMiles={geoMode === 'radius' ? watch('geoTargetRadiusMiles') : undefined}
          bypassEnabled={bypassFeasibility}
          onToggleBypass={onToggleBypassFeasibility}
        />
      </div>
    </div>
  );
};

const SingleMarketTargeting: React.FC<{
  config: PublisherConfig;
  accentColor: string;
  feasibility?: import('../../../services/feasibilityService').CampaignFeasibilityResponse | null;
  radiusRecommendation: import('../../../services/useRadiusRecommendation').UseRadiusRecommendationResult;
}> = ({ config, accentColor, feasibility, radiusRecommendation }) => {
  const { watch, setValue, formState: { errors } } = useFormContext<SelfServiceFormData>();
  const mode = (watch('targetingMode') ?? 'default') as 'default' | 'custom';
  const setMode = (next: 'default' | 'custom') => setValue('targetingMode', next, { shouldDirty: true });
  const market = config.markets[0];
  // CTV-533: empty-custom error attaches to `targetingMode` via the zod
  // resolver. Render it under the "Narrow down" option so the spatial
  // coupling with the control that produced it is preserved.
  const targetingModeError = errors.targetingMode?.message;

  const optionStyle = (active: boolean): React.CSSProperties => ({
    borderColor: active ? accentColor : undefined,
    backgroundColor: active ? tintFromAccent(accentColor, 0.06) : undefined,
    boxShadow: active
      ? `0 0 0 1px ${accentColor}, 0 6px 18px -10px ${tintFromAccent(accentColor, 0.5)}`
      : '0 1px 0 rgba(15,23,42,0.02)',
  });

  return (
    <div>
      <SectionHeading>Geo Targeting</SectionHeading>
      <div className="space-y-3">
        <button
          type="button"
          onClick={() => setMode('default')}
          className="w-full text-left border border-slate-200 rounded-2xl p-4 transition-all bg-white hover:border-slate-300"
          style={optionStyle(mode === 'default')}
        >
          <div className="flex items-start gap-3">
            <RadioDot active={mode === 'default'} accentColor={accentColor} />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" style={{ color: mode === 'default' ? accentColor : 'rgb(100,116,139)' }} />
                <span className="text-[14px] font-semibold tracking-tight text-slate-900">
                  Full Market Coverage
                </span>
              </div>
              <div className="text-[12px] text-slate-500 mt-1 ml-6 leading-relaxed">
                Advertise throughout the entire {market.channels.join(', ')} coverage area
              </div>
            </div>
          </div>
        </button>

        <button
          type="button"
          onClick={() => setMode('custom')}
          className="w-full text-left border border-slate-200 rounded-2xl p-4 transition-all bg-white hover:border-slate-300"
          style={optionStyle(mode === 'custom')}
        >
          <div className="flex items-start gap-3">
            <RadioDot active={mode === 'custom'} accentColor={accentColor} />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <MapIcon className="w-4 h-4" style={{ color: mode === 'custom' ? accentColor : 'rgb(100,116,139)' }} />
                <span className="text-[14px] font-semibold tracking-tight text-slate-900">
                  Target by Location
                </span>
              </div>
              <div className="text-[12px] text-slate-500 mt-1 ml-6 leading-relaxed">
                Target specific states, DMAs, or radius
              </div>
            </div>
          </div>
        </button>

        {mode === 'custom' && <FieldError message={targetingModeError} />}

        {mode === 'custom' && (
          <GeoTargetingTabs
            accentColor={accentColor}
            suggestedDMACodes={config.markets.map((m) => m.dmaCode)}
            suggestedStateCodes={Array.from(
              new Set(config.markets.flatMap((m) => m.coveredStates ?? [])),
            )}
            homeMarketCenter={config.homeMarketCenter ?? null}
            feasibility={feasibility}
            radiusRecommendation={radiusRecommendation}
          />
        )}
      </div>
    </div>
  );
};

const RadioDot: React.FC<{ active: boolean; accentColor: string }> = ({ active, accentColor }) => (
  <div
    className="mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors"
    style={{ borderColor: active ? accentColor : 'rgb(203, 213, 225)' }}
  >
    {active && <div className="w-2 h-2 rounded-full" style={{ backgroundColor: accentColor }} />}
  </div>
);

export default CampaignSetupStep;
